import { useState, useEffect } from "react";
import { RefreshCw, ExternalLink, WifiOff, Heart, MessageCircle, Users, Image } from "lucide-react";
import { MobileHeader } from "../components/MobileHeader";
import { MobileCard } from "../components/MobileCard";
import { instagramApi } from "../../api/instagram";
import type { DashboardResponse, MediaListResponse } from "../../modules/instagram/types";

function formatFollowers(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "今日";
  if (days === 1) return "1日前";
  if (days < 30) return `${days}日前`;
  const months = Math.floor(days / 30);
  return `${months}ヶ月前`;
}

/**
 * モバイル Instagram ページ
 * 読み取り専用ビュー：接続状態・アカウント情報・投稿一覧。
 * 投稿・下書き作成・コメント返信は PC 側で操作する。
 */
export const MobileInstagramPage = () => {
  const [tab, setTab] = useState<"overview" | "posts">("overview");
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [media, setMedia] = useState<MediaListResponse | null>(null);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);

  useEffect(() => {
    instagramApi.connection().then((conn) => {
      if (conn.connection && conn.status === "connected") {
        setConnected(true);
        instagramApi.dashboard().then((dash) => {
          setDashboard(dash);
          setLoading(false);
        }).catch(() => setLoading(false));
      } else {
        setConnected(false);
        setLoading(false);
      }
    }).catch(() => {
      setConnected(false);
      setLoading(false);
    });
  }, []);

  const loadMedia = () => {
    if (media) return;
    setLoadingMedia(true);
    instagramApi.listMedia({ limit: 12 }).then((m) => {
      setMedia(m);
      setLoadingMedia(false);
    }).catch(() => setLoadingMedia(false));
  };

  useEffect(() => {
    if (tab === "posts") loadMedia();
  }, [tab]);

  const handleRefresh = () => {
    if (!connected) return;
    setLoading(true);
    instagramApi.dashboard().then((dash) => {
      setDashboard(dash);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  return (
    <>
      <MobileHeader
        title="Instagram"
        rightAction={
          connected ? (
            <button
              type="button"
              className="mobile-appbar-icon-btn"
              onClick={handleRefresh}
              aria-label="更新"
              disabled={loading}
            >
              <RefreshCw size={18} strokeWidth={1.8} className={loading ? "spin" : ""} />
            </button>
          ) : undefined
        }
      />
      <main className="mobile-content">
        {/* 未接続 */}
        {connected === false && (
          <MobileCard>
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <WifiOff size={40} strokeWidth={1.4} color="var(--mobile-text-light)" style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 14, fontWeight: 600, color: "var(--mobile-text)", marginBottom: 6 }}>
                Instagram 未接続
              </p>
              <p style={{ fontSize: 12, color: "var(--mobile-text-sub)" }}>
                PC 側の「Instagram」ページから接続設定を行ってください。
              </p>
            </div>
          </MobileCard>
        )}

        {/* ローディング */}
        {loading && connected !== false && (
          <div style={{ textAlign: "center", padding: "32px 0", color: "var(--mobile-text-light)" }}>
            <RefreshCw size={24} strokeWidth={1.8} className="spin" />
            <p style={{ fontSize: 12, marginTop: 8 }}>読み込み中...</p>
          </div>
        )}

        {/* ダッシュボード */}
        {dashboard && !loading && (
          <>
            {/* タブ */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className={`mobile-filter-chip ${tab === "overview" ? "active" : ""}`}
                onClick={() => setTab("overview")}
              >
                データ総覧
              </button>
              <button
                type="button"
                className={`mobile-filter-chip ${tab === "posts" ? "active" : ""}`}
                onClick={() => setTab("posts")}
              >
                投稿一覧
              </button>
            </div>

            {tab === "overview" && (
              <>
                {/* アカウント */}
                <MobileCard>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {dashboard.account.profile_picture_url ? (
                      <img
                        src={dashboard.account.profile_picture_url}
                        alt={dashboard.account.username}
                        style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: "50%",
                          background: "var(--mobile-primary)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 18,
                          fontWeight: 700,
                        }}
                      >
                        {dashboard.account.username[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p style={{ fontSize: 15, fontWeight: 800, color: "var(--mobile-text)" }}>
                        {dashboard.account.username}
                      </p>
                      <p style={{ fontSize: 12, color: "var(--mobile-text-sub)" }}>
                        {dashboard.account.name}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 0, marginTop: 16, borderTop: "1px solid var(--mobile-divider)", paddingTop: 14 }}>
                    {[
                      { label: "フォロワー", value: formatFollowers(dashboard.account.followers_count) },
                      { label: "フォロー中", value: formatFollowers(dashboard.account.follows_count) },
                      { label: "投稿数", value: dashboard.account.media_count.toLocaleString() },
                    ].map((stat) => (
                      <div key={stat.label} style={{ flex: 1, textAlign: "center" }}>
                        <p style={{ fontSize: 17, fontWeight: 800, color: "var(--mobile-text)" }}>{stat.value}</p>
                        <p style={{ fontSize: 11, color: "var(--mobile-text-sub)", marginTop: 2 }}>{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </MobileCard>

                {/* KPI */}
                <div className="mobile-stat-grid">
                  <div className="mobile-stat-card">
                    <div className="mobile-stat-card-header">
                      <div className="mobile-stat-card-icon" style={{ background: "#eff6ff", color: "#2563eb" }}>
                        <Users size={16} strokeWidth={2} />
                      </div>
                      <span className="mobile-stat-card-trend up">
                        <Heart size={11} strokeWidth={2.5} />
                        {dashboard.metrics.followers > 0 ? "+" : ""}
                        {dashboard.metrics.followers}
                      </span>
                    </div>
                    <div className="mobile-stat-card-label">フォロワー増</div>
                    <div>
                      <span className="mobile-stat-card-value">{dashboard.metrics.followers >= 0 ? "+" : ""}{dashboard.metrics.followers}</span>
                    </div>
                  </div>

                  <div className="mobile-stat-card">
                    <div className="mobile-stat-card-header">
                      <div className="mobile-stat-card-icon" style={{ background: "#fee2e2", color: "#ef4444" }}>
                        <MessageCircle size={16} strokeWidth={2} />
                      </div>
                      <span className="mobile-badge yellow" style={{ fontSize: 10 }}>
                        {dashboard.metrics.pending_comments}
                      </span>
                    </div>
                    <div className="mobile-stat-card-label">未対応コメント</div>
                    <div>
                      <span className="mobile-stat-card-value">{dashboard.metrics.new_comments}</span>
                    </div>
                  </div>
                </div>

                {/* アクティビティ */}
                {dashboard.recent_activities.length > 0 && (
                  <MobileCard title="最新アクティビティ">
                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      {dashboard.recent_activities.slice(0, 5).map((a, i) => (
                        <div
                          key={a.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 0",
                            borderBottom: i < dashboard.recent_activities.slice(0, 5).length - 1 ? "1px solid var(--mobile-divider)" : "none",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: "var(--mobile-text)" }}>{a.title}</p>
                            <p style={{ fontSize: 11, color: "var(--mobile-text-sub)", marginTop: 2 }}>{a.description}</p>
                          </div>
                          <span style={{ fontSize: 11, color: "var(--mobile-text-light)", marginLeft: 8 }}>{timeAgo(a.occurred_at)}</span>
                        </div>
                      ))}
                    </div>
                  </MobileCard>
                )}

                {/* PC 遷移ヒント */}
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <a
                    href="/instagram-v2"
                    style={{ fontSize: 12, color: "var(--mobile-primary)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    PC版 Instagram を見る <ExternalLink size={11} strokeWidth={2} />
                  </a>
                </div>
              </>
            )}

            {tab === "posts" && (
              <>
                {loadingMedia && (
                  <div style={{ textAlign: "center", padding: "24px 0", color: "var(--mobile-text-light)" }}>
                    <RefreshCw size={20} strokeWidth={1.8} className="spin" />
                    <p style={{ fontSize: 12, marginTop: 8 }}>読み込み中...</p>
                  </div>
                )}

                {media && (
                  <>
                    {media.items.length === 0 ? (
                      <MobileCard>
                        <div style={{ textAlign: "center", padding: "16px 0", color: "var(--mobile-text-light)" }}>
                          <Image size={32} strokeWidth={1.4} style={{ marginBottom: 8 }} />
                          <p style={{ fontSize: 13 }}>投稿がありません</p>
                        </div>
                      </MobileCard>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 2,
                        }}
                      >
                        {media.items.map((item) => (
                          <a
                            key={item.id}
                            href={item.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "block",
                              aspectRatio: "1",
                              overflow: "hidden",
                              borderRadius: 4,
                              position: "relative",
                              background: "var(--mobile-bg)",
                            }}
                          >
                            {item.thumbnail_url || item.media_url ? (
                              <img
                                src={item.thumbnail_url || item.media_url || ""}
                                alt={item.caption.slice(0, 50)}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "var(--mobile-text-light)",
                                }}
                              >
                                <Image size={24} strokeWidth={1.4} />
                              </div>
                            )}
                            <div
                              style={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: "rgba(0,0,0,0.45)",
                                display: "flex",
                                justifyContent: "space-around",
                                padding: "4px 0",
                              }}
                            >
                              <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
                                <Heart size={11} strokeWidth={2.5} />{item.like_count}
                              </span>
                              <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
                                <MessageCircle size={11} strokeWidth={2.5} />{item.comments_count}
                              </span>
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </>
  );
};
